import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { useAppStore } from '@/core/store/app.store';

import { dispatchPlacesService } from './dispatch-places.services';

import type {
  CreateDispatchPlacePayload,
  DispatchPlace,
  DispatchPlaceHistoryResponse,
  DispatchPlaceListResponse,
  DispatchPlaceMetricsResponse,
  DispatchPlaceSummary,
  DispatchPlaceUser,
} from './dispatch-places.types';

/**
 * Query Key Factory for the Dispatch Places domain.
 */
export const dispatchPlacesQueryKeys = {
  all: ['dispatch-places'] as const,
  list: () => [...dispatchPlacesQueryKeys.all, 'list'] as const,
  detail: (id: string) => [...dispatchPlacesQueryKeys.all, 'detail', id] as const,
  summary: () => [...dispatchPlacesQueryKeys.all, 'summary'] as const,
  /**
   * Keyed by country so the cache is invalidated automatically when the
   * country switcher changes (the backend scopes by X-Country-Code).
   */
  metrics: (country: string) => [...dispatchPlacesQueryKeys.all, 'metrics', country] as const,
  /**
   * Keyed by both dp_id and country so the cache refreshes on place or
   * country change.
   */
  users: (dp_id: string, country: string) =>
    [...dispatchPlacesQueryKeys.all, 'users', dp_id, country] as const,
  history: (dp_id: string, country: string) =>
    [...dispatchPlacesQueryKeys.all, 'history', dp_id, country] as const,
};

/**
 * Returns the full list of dispatch places from the real backend.
 * Endpoint: GET /admin/dispatch_places
 */
export const useGetDispatchPlaces = () => {
  return useQuery({
    queryKey: dispatchPlacesQueryKeys.list(),
    queryFn: (): Promise<DispatchPlaceListResponse> => dispatchPlacesService.list(),
  });
};

/**
 * Returns a single dispatch place by its ID.
 * Endpoint: GET /admin/dispatch_places/:id
 */
export const useGetDispatchPlaceById = (id: string) => {
  return useQuery({
    queryKey: dispatchPlacesQueryKeys.detail(id),
    queryFn: (): Promise<DispatchPlace> => dispatchPlacesService.getById(id),
    enabled: !!id,
  });
};

/**
 * Returns aggregated counts (total, active, inactive, pending) for the
 * dispatch places summary cards from the real backend.
 * Endpoint: GET /admin/dispatch_places/summary
 */
export const useGetDispatchPlaceSummary = () => {
  return useQuery({
    queryKey: dispatchPlacesQueryKeys.summary(),
    queryFn: (): Promise<DispatchPlaceSummary> => dispatchPlacesService.getSummary(),
  });
};

/**
 * Returns per-dispatch-place order counts grouped by status bucket.
 * Endpoint: GET /admin/dispatch_places/metrics
 *
 * The country is included in the query key so the cache is invalidated
 * whenever the active country changes in the app store.
 */
export const useGetDispatchPlacesMetrics = () => {
  const country = useAppStore((state) => state.country);

  return useQuery({
    queryKey: dispatchPlacesQueryKeys.metrics(country),
    queryFn: (): Promise<DispatchPlaceMetricsResponse> => dispatchPlacesService.getMetrics(),
  });
};

/**
 * Returns the list of users associated with a dispatch place.
 * Endpoint: GET /admin/dispatch_places/:id/users
 */
export const useGetDispatchPlaceUsers = (dp_id: string) => {
  const country = useAppStore((state) => state.country);

  return useQuery({
    queryKey: dispatchPlacesQueryKeys.users(dp_id, country),
    queryFn: (): Promise<DispatchPlaceUser[]> => dispatchPlacesService.getUsers(dp_id),
    enabled: !!dp_id,
  });
};

/**
 * Returns the ordered change log for a dispatch place.
 * Endpoint: GET /admin/dispatch_places/:id/history
 */
export const useGetDispatchPlaceHistory = (dp_id: string) => {
  const country = useAppStore((state) => state.country);

  return useQuery({
    queryKey: dispatchPlacesQueryKeys.history(dp_id, country),
    queryFn: (): Promise<DispatchPlaceHistoryResponse> =>
      dispatchPlacesService.getHistory(dp_id),
    enabled: !!dp_id,
  });
};

export const useCreateDispatchPlace = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (payload: CreateDispatchPlacePayload) => dispatchPlacesService.create(payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: dispatchPlacesQueryKeys.list() });
      queryClient.invalidateQueries({ queryKey: dispatchPlacesQueryKeys.summary() });
    },
  });
};
